import React from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import { loadKioskPairing, approveKioskPairing, denyKioskPairing } from '../../actions/kioskActions';
import { getErrorMessage, mustBeArray } from '../../utils/lib';

// performFetch puts a 200 body on .data and any other status's parsed body on .errors. Both matter
// here: an ambiguous truck comes back as a 409 whose body carries the runs to choose between.
const readBody = response => (response && response.errors) || (response && response.data) || null;

/**
 * The confirm screen a driver lands on after scanning the QR on a truck console.
 *
 * Runs on the driver's own phone, inside the normal signed-in app — approving here is what turns
 * their B2C identity into a console session. The truck is named prominently on purpose: it is what
 * stops someone approving the wrong console, and makes a QR stuck somewhere it shouldn't be obvious.
 */
class PairConfirmPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            pairing: null,
            error: null,
            submitting: false,
            candidates: [],
            selectedRunId: null,
        };
    }

    componentDidMount() {
        this.load();
    }

    load() {
        const { dispatch, userCode } = this.props;

        return dispatch(loadKioskPairing(userCode)).then(response => {
            const body = readBody(response);

            if (!body || !body.success) {
                this.setState({
                    loading: false,
                    error: getErrorMessage({ x: response })
                        || 'That code has expired or has already been used. Ask the console for a new one.',
                });
                return;
            }

            const pairing = body.pairing || {};
            const candidates = mustBeArray(pairing.candidates);

            this.setState({
                loading: false,
                pairing,
                candidates,
                // Pre-select the driver's own run when there is exactly one, so the common case is a
                // single tap.
                selectedRunId: candidates.length === 1
                    ? candidates[0].runId
                    : (candidates.filter(c => c.isAssignedToApprover)[0] || {}).runId || null,
            });
        });
    }

    approve = () => {
        const { dispatch, userCode, navigate } = this.props;
        const { selectedRunId } = this.state;

        this.setState({ submitting: true });

        return dispatch(approveKioskPairing(userCode, selectedRunId)).then(response => {
            const body = readBody(response);

            if (body && body.requiresRunSelection) {
                // More than one run on this truck and we can't tell which — ask rather than guess.
                this.setState({
                    submitting: false,
                    candidates: mustBeArray(body.candidates),
                    error: body.message,
                });
                return;
            }

            if (!body || !body.success) {
                this.setState({ submitting: false });
                toastr.error('Sign in failed', getErrorMessage({ x: response }) || 'Could not sign in to the console.');
                return;
            }

            toastr.success('Signed in', body.message || `You're signed in at the console on ${body.truckNumberPlate}.`);
            navigate('/runs/driver', { replace: true });
        });
    };

    deny = () => {
        const { dispatch, userCode, navigate } = this.props;

        this.setState({ submitting: true });

        return dispatch(denyKioskPairing(userCode)).then(() => {
            toastr.info('Sign in cancelled', 'The console was not signed in.');
            navigate('/runs/driver', { replace: true });
        });
    };

    renderCandidates() {
        const { candidates, selectedRunId } = this.state;

        if (candidates.length < 2) {
            return null;
        }

        return (
            <div className="form-group">
                <label><strong>Which run are you driving?</strong></label>
                {candidates.map(candidate => (
                    <div className="form-check" key={candidate.runId}>
                        <label className="form-check-label">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="runId"
                                checked={selectedRunId === candidate.runId}
                                onChange={() => this.setState({ selectedRunId: candidate.runId })}
                            />
                            {' '}
                            Run {candidate.runId}
                            {candidate.runName ? ` — ${candidate.runName}` : ''}
                            {' '}
                            <small className="text-muted">
                                ({moment(candidate.timeStart).format('h:mm a')}
                                {candidate.isAssignedToApprover
                                    ? ', yours'
                                    : `, rostered to ${candidate.assignedDriverName}`})
                            </small>
                        </label>
                    </div>
                ))}
            </div>
        );
    }

    renderClaimWarning() {
        const { candidates, selectedRunId } = this.state;
        const selected = candidates.filter(c => c.runId === selectedRunId)[0];

        if (!selected || selected.isAssignedToApprover) {
            return null;
        }

        return (
            <div className="alert alert-warning">
                Run {selected.runId} is rostered to <strong>{selected.assignedDriverName}</strong>.
                Signing in moves it to you and lets the office know.
                {selected.hasCompletedWork
                    ? ` Pickups already completed stay recorded against ${selected.assignedDriverName}.`
                    : ''}
            </div>
        );
    }

    render() {
        const { loading, pairing, error, submitting, candidates, selectedRunId } = this.state;

        if (loading) {
            return <div className="container pt-4">Loading…</div>;
        }

        if (!pairing) {
            return (
                <div className="container pt-4">
                    <div className="alert alert-danger">{error}</div>
                </div>
            );
        }

        // Can't approve until the driver has told us which run, when it's ambiguous.
        const blockedOnSelection = candidates.length > 1 && !selectedRunId;

        return (
            <div className="container pt-4" style={{ maxWidth: '540px' }}>
                <h4>Sign in to the truck console</h4>

                <div className="card mb-3">
                    <div className="card-body text-center">
                        <div className="text-muted">Truck</div>
                        <div style={{ fontSize: '2rem', fontWeight: 700, letterSpacing: '2px' }}>
                            {pairing.truckNumberPlate}
                        </div>
                    </div>
                </div>

                <p>
                    Check this is the truck you&apos;re standing at before you continue. The code expires{' '}
                    {moment(pairing.expiresAt).fromNow()}.
                </p>

                {error ? <div className="alert alert-warning">{error}</div> : null}

                {this.renderCandidates()}
                {this.renderClaimWarning()}

                {candidates.length === 0 ? (
                    <div className="alert alert-info">
                        There&apos;s no active run on this truck. You can still sign in — the office
                        will be told.
                    </div>
                ) : null}

                <button
                    className="btn btn-primary btn-lg btn-block"
                    disabled={submitting || blockedOnSelection}
                    onClick={this.approve}>
                    {submitting ? 'Signing in…' : `Sign in on ${pairing.truckNumberPlate}`}
                </button>

                <button
                    className="btn btn-link btn-block"
                    disabled={submitting}
                    onClick={this.deny}>
                    That&apos;s not my truck
                </button>
            </div>
        );
    }
}

// Functional wrapper: this is a class component (matching the surrounding code) but needs the v6
// router hooks for the :code param and navigation.
function PairConfirmPageContainer(props) {
    const { code } = useParams();
    const navigate = useNavigate();

    return <PairConfirmPage {...props} userCode={code} navigate={navigate} />;
}

export default connect()(PairConfirmPageContainer);
